import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { jobsApi, feesApi, ApiError } from "@/api";
import type { OrderBookResponse, OrderBookJob, OrderBookFee } from "@/types";
import { Button, Spinner, Pill, Toast, useToast } from "@/components/ui";
import { feeTypeTone, feeStatusTone, humanise, jobStageToPill, buildJobAddressString } from "@/lib/jobs";
import { formatMoney } from "@/lib/money";
import "./jobs.css";
import "./order-book.css";

type DeleteTarget = { job: OrderBookJob; fee: OrderBookFee };

/** Jobs › Order book — a cross-job view of every LIVE job with uninvoiced fees
 *  (the work agreed but not yet billed). Each job card lists its uninvoiced fees
 *  separately; each fee has a Delete button that opens a confirm dialog before
 *  deleting (the server refuses fees that have been invoiced, so those buttons
 *  are disabled). The headline total uses the identical calc as the overview
 *  dashboard's order-book KPI, so the two always agree. After a delete we do a
 *  silent re-fetch so the total stays the server-authoritative figure. */
export function OrderBook() {
  const [data, setData] = useState<OrderBookResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { toast, showToast } = useToast();

  const fetchData = useCallback((opts: { initial?: boolean } = {}) => {
    let cancelled = false;
    if (opts.initial) setLoading(true);
    setError(undefined);
    jobsApi
      .getOrderBook()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the order book.");
      })
      .finally(() => {
        if (!cancelled && opts.initial) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => fetchData({ initial: true }), [fetchData]);

  async function confirmDelete() {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await feesApi.deleteFee(deleteTarget.fee.id);
      showToast(`Fee deleted — ${deleteTarget.fee.description}.`);
      setDeleteTarget(null);
      // Silent re-fetch: the total is recomputed server-side, so it stays exactly
      // in step with the overview dashboard KPI.
      fetchData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the fee.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className="order-book">
      <Link to="/staff/jobs" className="order-book__back">← Jobs</Link>

      <header className="order-book__head">
        <div>
          <h1>Order book</h1>
          <p className="order-book__sub">
            Uninvoiced fees across every live job — the work you&rsquo;ve agreed but not yet billed.
          </p>
        </div>
        {data ? (
          <div className="order-book__total" title="Matches the order-book KPI on the overview dashboard">
            <span className="order-book__total-value numeric">{formatMoney(data.total)}</span>
            <span className="order-book__total-label">
              uninvoiced · {data.jobs_count} job{data.jobs_count === 1 ? "" : "s"}
            </span>
          </div>
        ) : null}
      </header>

      {error ? (
        <p className="leads__error" role="alert" onClick={() => setError(undefined)}>
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="order-book__loading">
          <Spinner label="Loading order book…" />
        </div>
      ) : !data || data.jobs.length === 0 ? (
        <p className="order-book__empty">No uninvoiced fees right now — every live job is fully invoiced.</p>
      ) : (
        <div className="order-book__list">
          {data.jobs.map((job) => (
            <OrderBookCard key={job.job_id} job={job} onDelete={(fee) => setDeleteTarget({ job, fee })} />
          ))}
        </div>
      )}

      {deleteTarget ? (
        <div className="job-modal__overlay" onClick={deleting ? undefined : () => setDeleteTarget(null)}>
          <div className="job-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Delete fee">
            <header className="job-modal__head"><h3>Delete this fee?</h3></header>
            <div className="job-modal__body">
              <p className="job-drawer__muted">
                This permanently deletes the fee from job <span className="numeric">{deleteTarget.job.job_number}</span> and
                removes it from the order book. This can&rsquo;t be undone.
              </p>
              <div className="order-book__confirm-fee">
                <span className="order-book__confirm-desc">{deleteTarget.fee.description}</span>
                <span className="numeric">{formatMoney(deleteTarget.fee.uninvoiced)}</span>
              </div>
            </div>
            <footer className="job-modal__foot">
              <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Keep it</Button>
              <Button variant="danger" onClick={confirmDelete} loading={deleting}>Delete fee</Button>
            </footer>
          </div>
        </div>
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}

function OrderBookCard({ job, onDelete }: { job: OrderBookJob; onDelete: (fee: OrderBookFee) => void }) {
  const address = buildJobAddressString(job);
  const stage = jobStageToPill(job.status);
  const title = job.job_name || address || "Untitled job";
  // Only show the address as a second line when it isn't already the title.
  const showAddress = address && address !== title;

  return (
    <article className="order-book__job">
      <header className="order-book__job-head">
        <div className="order-book__job-ident">
          <Link to={`/staff/jobs/${job.job_id}`} className="order-book__job-link">
            <span className="numeric">{job.job_number}</span> — {title}
          </Link>
          <div className="order-book__job-meta">
            {job.client_name ? <span className="order-book__job-client">{job.client_name}</span> : null}
            {showAddress ? (
              <span className="order-book__job-addr">
                {address}{job.postcode ? `, ${job.postcode}` : ""}
              </span>
            ) : null}
            <Pill tone={stage.tone}>{stage.label}</Pill>
          </div>
        </div>
        <div className="order-book__remaining">
          <span className="order-book__remaining-value numeric">{formatMoney(job.remaining)}</span>
          <span className="order-book__remaining-label">uninvoiced</span>
        </div>
      </header>

      <ul className="order-book__fees">
        {job.fees.map((fee) => (
          <li key={fee.id} className="order-book__fee">
            <div className="order-book__fee-main">
              <div className="order-book__fee-title">
                <span className="order-book__fee-desc">{fee.description}</span>
                <Pill tone={feeTypeTone(fee.fee_type)}>{humanise(fee.fee_type)}</Pill>
                <Pill tone={feeStatusTone(fee.status)}>{humanise(fee.status)}</Pill>
              </div>
              <div className="order-book__fee-sub job-drawer__muted">
                {fee.amount_invoiced > 0 ? (
                  <>{formatMoney(fee.amount)} agreed · {formatMoney(fee.amount_invoiced)} invoiced</>
                ) : (
                  <>{formatMoney(fee.amount)} agreed</>
                )}
                {fee.notes ? <span className="order-book__fee-note"> · {fee.notes}</span> : null}
              </div>
            </div>
            <div className="order-book__fee-right">
              <span className="order-book__fee-amount numeric">{formatMoney(fee.uninvoiced)}</span>
              <Button
                variant="danger"
                size="sm"
                disabled={!fee.deletable}
                title={fee.deletable ? "Delete this fee" : "Already invoiced — can’t delete"}
                onClick={() => onDelete(fee)}
              >
                Delete
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </article>
  );
}
